import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ContentContext } from "../context/ContentProvider";
import {
  Grid,
  Button,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Save as SaveIcon,
} from "@mui/icons-material";
import DashboardCard from "./DashboardCard";
import RichTextEditor from "./RichTextEditor";

function ActivitiesForm() {
  const { content, setContent } = useContext(ContentContext);
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Submit handler that navigates to /activities after submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log("Submitting:", content.activitiesText, content.activitiesStyle);

    try {
      const response = await fetch(`${apiUrl}/api/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          activitiesText: content.activitiesText,
          activitiesStyle: content.activitiesStyle,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Update the context with data returned by the server.
        setContent(data);
        setSnackbar({ open: true, message: "Activities content saved successfully!", severity: "success" });
        // Navigate to the Activities page after successful submission.
        setTimeout(() => navigate("/activities"), 1500);
      } else {
        const errorData = await response.json();
        console.error("Failed to submit content:", errorData);
        setSnackbar({ open: true, message: "Failed to save content. Please try again.", severity: "error" });
      }
    } catch (error) {
      console.error("Error during POST:", error);
      setSnackbar({ open: true, message: "Network error. Please check your connection.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Handler for updating the text input
  // Rich text editor passes HTML string directly, not an event
  const handleTextChange = (htmlContent) => {
    setContent({ ...content, activitiesText: htmlContent });
  };

  // Handler for updating the text color
  const handleColorChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, color: e.target.value },
    });
  };

  // Handler for toggling bold style
  const handleBoldChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, bold: e.target.checked },
    });
  };

  // Handler for toggling italic style
  const handleItalicChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, italic: e.target.checked },
    });
  };

  // Handler for toggling underline style
  const handleUnderlineChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, underline: e.target.checked },
    });
  };

  // Handler for text alignment
  const handleAlignmentChange = (alignment) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, textAlign: alignment },
    });
  };

  // Handler for font size
  const handleFontSizeChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, fontSize: e.target.value },
    });
  };

  // Handler for font family
  const handleFontFamilyChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, fontFamily: e.target.value },
    });
  };

  // Handler for background color
  const handleBackgroundColorChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, backgroundColor: e.target.value },
    });
  };

  // Handler for line height
  const handleLineHeightChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, lineHeight: e.target.value },
    });
  };

  // Handler for letter spacing
  const handleLetterSpacingChange = (e) => {
    setContent({
      ...content,
      activitiesStyle: { ...content.activitiesStyle, letterSpacing: e.target.value },
    });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Edit Form */}
        <Grid item xs={12} md={6}>
          <DashboardCard title="Edit Activities Content" icon={<SaveIcon />}>
            <Box component="form" onSubmit={handleSubmit}>
              {/* Rich Text Editor */}
              <Box sx={{ mb: 3 }}>
                <RichTextEditor
                  value={content.activitiesText || ""}
                  onChange={handleTextChange}
                  placeholder="Start typing your activities content here... Select text to format it!"
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save & Preview"}
              </Button>
            </Box>
          </DashboardCard>
        </Grid>

        {/* Preview */}
        <Grid item xs={12} md={6}>
          <DashboardCard title="Live Preview">
            <Paper
              elevation={0}
              sx={{
                p: 4,
                minHeight: 600,
                maxHeight: 800,
                overflowY: 'auto',
                backgroundColor: 'action.hover',
                borderRadius: 2,
              }}
            >
              <Box
                dangerouslySetInnerHTML={{
                  __html: content.activitiesText || "<p style='color: #999; font-style: italic;'>Your activities content will appear here...</p>"
                }}
                sx={{
                  '& *': {
                    fontFamily: 'inherit',
                  }
                }}
              />
            </Paper>
          </DashboardCard>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ActivitiesForm;
